import React, { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import AddDepartment from "./AddDepartment";

function DepartmentList() {
  const [departments, setDepartments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);

  // ✅ Fetch Tenants
  const fetchTenants = async () => {
    try {
      const res = await axiosClient.get("/tenant/api/list");
      setTenants(res.data.data || []);
    } catch (err) {
      console.error("Error fetching tenants:", err);
      alert("Unable to load tenants");
    }
  };

  // ✅ Fetch Locations (filtered by tenant)
  const fetchLocations = async () => {
    if (!selectedTenant) {
      setLocations([]);
      return;
    }
    try {
      const res = await axiosClient.get(
        `/tenant/api/locations/list?tenant_id=${selectedTenant}`
      );
      setLocations(res.data.data || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
      alert("Unable to load locations");
    }
  };

  // ✅ Fetch Departments (filtered by tenant/location)
  const fetchDepartments = async () => {
    try {
      let url = "/tenant/api/departments/list";
      if (selectedTenant)
        url += `?tenant_id=${selectedTenant}${
          selectedLocation ? `&location_id=${selectedLocation}` : ""
        }`;
      const res = await axiosClient.get(url);
      setDepartments(res.data.data || []);
    } catch (err) {
      console.error("Error fetching departments:", err);
      alert("Unable to load departments");
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchDepartments();
  }, [selectedTenant, selectedLocation]);

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold text-primary">🏬 Departments</h4>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddModal(true)}
          style={{
            background: "linear-gradient(45deg, #007bff, #0056b3)",
            border: "none",
          }}
        >
          ➕ Add Department
        </button>
      </div>

      {/* Tenant Filter */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">Select Tenant:</label>
          <select
            className="form-select"
            value={selectedTenant}
            onChange={(e) => {
              setSelectedTenant(e.target.value);
              setSelectedLocation("");
            }}
          >
            <option value="">All Tenants</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">Select Location:</label>
          <select
            className="form-select"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            disabled={!selectedTenant}
          >
            <option value="">All Locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-primary">
            <tr>
              <th>Tenant</th>
              <th>Location</th>
              <th>Name</th>
              <th>Code</th>
              <th>Description</th>
              <th>Status</th>
              <th style={{ width: "220px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {departments.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted py-3">
                  No departments found
                </td>
              </tr>
            ) : (
              departments.map((d) => (
                <tr key={d.id}>
                  <td>{tenants.find((t) => t.id === d.tenant_id)?.name || "—"}</td>
                  <td>
                    {locations.find((l) => l.id === d.location_id)?.name || "—"}
                  </td>
                  <td>{d.name}</td>
                  <td>{d.code}</td>
                  <td>{d.description || "—"}</td>
                  <td>
                    <span
                      className={`badge ${
                        d.status === "Active" ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="d-flex gap-2">
                    {/* ✏️ Edit */}
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setEditingDept(d)}
                    >
                      ✏️ Edit
                    </button>

                    {/* 🔁 Activate/Deactivate */}
                    <button
                      className={`btn btn-sm ${
                        d.status === "Active" ? "btn-warning" : "btn-success"
                      }`}
                      onClick={async () => {
                        try {
                          await axiosClient.put(
                            `/tenant/api/departments/${d.id}/toggle`
                          );
                          fetchDepartments();
                        } catch {
                          alert("Failed to change status");
                        }
                      }}
                    >
                      {d.status === "Active" ? "Deactivate" : "Activate"}
                    </button>

                    {/* 🗑 Delete */}
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={async () => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete department "${d.name}"?`
                          )
                        ) {
                          try {
                            await axiosClient.delete(
                              `/tenant/api/departments/${d.id}`
                            );
                            fetchDepartments();
                          } catch {
                            alert("Failed to delete department");
                          }
                        }
                      }}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddDepartment
          tenants={tenants}
          locations={locations}
          selectedTenant={selectedTenant}
          onClose={() => {
            setShowAddModal(false);
            fetchDepartments();
          }}
        />
      )}

      {/* Edit Modal */}
      {editingDept && (
        <AddDepartment
          tenants={tenants}
          locations={locations}
          selectedTenant={selectedTenant}
          existing={editingDept}
          onClose={() => {
            setEditingDept(null);
            fetchDepartments();
          }}
        />
      )}
    </div>
  );
}

export default DepartmentList;
